import {
  useQuery,
  useMutation,
  useInfiniteQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { useAuth } from '@teletienda/shared-auth-ui';
import {
  conversationKeys,
  type ConversationListItem,
} from '@teletienda/shared-ui-lib';
import { conversationsApi } from '@/lib/api/conversations';
import type {
  Message,
  SendMessageDto,
  CreateConversationDto,
  ConversationsQueryParams,
} from '@/types/messenger';

// Re-export for consumers that import from this module
export { conversationKeys };

// --- Conversations List ---

export function useConversations(params?: ConversationsQueryParams) {
  const { isAuthenticated } = useAuth();

  return useQuery({
    queryKey: conversationKeys.list(params),
    queryFn: () => conversationsApi.list(params),
    enabled: isAuthenticated,
    staleTime: 30 * 1000,
  });
}

// --- Single Conversation ---

export function useConversation(conversationGuid: string | undefined) {
  const { isAuthenticated } = useAuth();

  return useQuery({
    queryKey: conversationKeys.detail(conversationGuid!),
    queryFn: () => conversationsApi.get(conversationGuid!),
    enabled: isAuthenticated && !!conversationGuid,
    staleTime: 60 * 1000,
  });
}

// --- Messages (Infinite Scroll) ---

export function useMessages(conversationGuid: string | undefined) {
  const { isAuthenticated } = useAuth();

  return useInfiniteQuery({
    queryKey: conversationKeys.messages(conversationGuid!),
    queryFn: ({ pageParam }) =>
      conversationsApi.getMessages(conversationGuid!, {
        limit: 30,
        before: pageParam as string | undefined,
      }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => {
      if (!lastPage || lastPage.length < 30) return undefined;
      // Cursor = oldest message ID in last page
      return lastPage[lastPage.length - 1]?.message_id;
    },
    enabled: isAuthenticated && !!conversationGuid,
    staleTime: Infinity,
  });
}

// --- Send Message (Optimistic) ---

export function useSendMessage(conversationGuid: string) {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: (data: SendMessageDto) =>
      conversationsApi.sendMessage(conversationGuid, data),

    onMutate: async (newMessage) => {
      const messagesKey = conversationKeys.messages(conversationGuid);
      await queryClient.cancelQueries({ queryKey: messagesKey });

      const previousMessages = queryClient.getQueryData(messagesKey);

      // Optimistic message
      const optimisticMessage: Message = {
        message_id: `temp-${Date.now()}`,
        conversation_id: conversationGuid,
        sender_guid: user?.userGuid || '',
        sender_type: 'user',
        message_type: newMessage.message_type || 'text',
        content: newMessage.content,
        attachments: newMessage.attachments || [],
        status: 'sending',
        read_at: null,
        created_at: new Date().toISOString(),
      };

      queryClient.setQueryData(messagesKey, (old: any) => {
        if (!old) return { pages: [[optimisticMessage]], pageParams: [undefined] };
        return {
          ...old,
          pages: [[optimisticMessage, ...old.pages[0]], ...old.pages.slice(1)],
        };
      });

      return { previousMessages };
    },

    onError: (_err, _variables, _context) => {
      // Mark optimistic message as 'failed' (keeps it visible with retry button)
      const messagesKey = conversationKeys.messages(conversationGuid);
      queryClient.setQueryData(messagesKey, (old: any) => {
        if (!old) return old;
        const firstPage = old.pages[0].map((msg: Message) =>
          msg.message_id.startsWith('temp-') ? { ...msg, status: 'failed' } : msg
        );
        return { ...old, pages: [firstPage, ...old.pages.slice(1)] };
      });
    },

    onSuccess: (sentMessage, variables) => {
      const messagesKey = conversationKeys.messages(conversationGuid);

      if (variables.context) {
        // Context sends create a system_notification + user message on the backend.
        // The API only returns the user message, so invalidate the cache to refetch
        // both messages (system_notification will appear above the user's message).
        queryClient.invalidateQueries({ queryKey: messagesKey });
      } else {
        // Replace optimistic message with real one
        queryClient.setQueryData(messagesKey, (old: any) => {
          if (!old) return old;
          const firstPage = old.pages[0].map((msg: Message) =>
            msg.message_id.startsWith('temp-') ? sentMessage : msg
          );
          return { ...old, pages: [firstPage, ...old.pages.slice(1)] };
        });
      }

      // Update conversation list (last message preview)
      queryClient.invalidateQueries({ queryKey: conversationKeys.lists() });
    },
  });
}

// --- Create Conversation ---

export function useCreateConversation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateConversationDto) => conversationsApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: conversationKeys.lists() });
    },
  });
}

// --- Mark As Read ---

export function useMarkAsRead(conversationGuid: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => conversationsApi.markAsRead(conversationGuid),

    onMutate: async () => {
      // Optimistically set unreadCount to 0 in conversations list
      await queryClient.cancelQueries({ queryKey: conversationKeys.lists() });

      const previousLists = queryClient.getQueriesData<ConversationListItem[]>({
        queryKey: conversationKeys.lists(),
      });

      queryClient.setQueriesData<ConversationListItem[]>(
        { queryKey: conversationKeys.lists() },
        (old) => {
          if (!old) return old;
          return old.map((conv) =>
            conv.conversationId === conversationGuid
              ? { ...conv, unreadCount: 0, hasUnreadMessages: false }
              : conv
          );
        }
      );

      // Also patch detail query to prevent useEffect(hasUnreadMessages) re-trigger
      const previousDetail = queryClient.getQueryData(
        conversationKeys.detail(conversationGuid)
      );
      queryClient.setQueryData(
        conversationKeys.detail(conversationGuid),
        (old: any) => old ? { ...old, hasUnreadMessages: false, unreadCount: 0 } : old
      );

      return { previousLists, previousDetail };
    },

    onError: (_err, _variables, context) => {
      if (context?.previousLists) {
        for (const [key, data] of context.previousLists) {
          queryClient.setQueryData(key, data);
        }
      }
      if (context?.previousDetail) {
        queryClient.setQueryData(
          conversationKeys.detail(conversationGuid),
          context.previousDetail
        );
      }
    },

    onSuccess: () => {
      // Update the header badge unread count
      queryClient.invalidateQueries({
        queryKey: conversationKeys.unreadTotal(),
      });
    },
  });
}

// --- Close Conversation ---

export function useCloseConversation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      conversationGuid,
      reason,
    }: {
      conversationGuid: string;
      reason?: string;
    }) => conversationsApi.close(conversationGuid, reason),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: conversationKeys.lists() });
    },
  });
}

// --- Update Conversation ---

export function useUpdateConversation(conversationGuid: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: Parameters<typeof conversationsApi.update>[1]) =>
      conversationsApi.update(conversationGuid, data),
    onSuccess: (updated) => {
      queryClient.setQueryData(conversationKeys.detail(conversationGuid), updated);
      queryClient.invalidateQueries({ queryKey: conversationKeys.lists() });
    },
  });
}

// --- Unread Total (derived) ---

export function useUnreadTotal() {
  const { isAuthenticated } = useAuth();

  return useQuery({
    queryKey: conversationKeys.unreadTotal(),
    queryFn: async () => {
      const conversations = await conversationsApi.list({ status: 'active' });
      if (!conversations) return 0;
      return conversations.reduce(
        (sum: number, conv: ConversationListItem) => sum + conv.unreadCount,
        0
      );
    },
    enabled: isAuthenticated,
    staleTime: 30 * 1000,
  });
}
